// Helpers for firing Meta Pixel events reliably with deduplication.

import { META_PIXEL_ID } from "./meta-pixel-config";

const STORAGE_KEY = "meta_lead_event_v1";

type StoredLead = {
  eventID: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  /** Routing tier ("3" = qualified/demo, "2" = intro, "DQ") — used to gate Schedule events. */
  tier?: string;
  outcome?: string;
  ts: number;
};

type FbqFn = (...args: unknown[]) => void;

function getFbq(): FbqFn | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { fbq?: FbqFn };
  return typeof w.fbq === "function" ? w.fbq : null;
}

function newEventID(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `lead-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function persistLeadEvent(payload: Omit<StoredLead, "ts" | "eventID"> & { eventID?: string }): StoredLead {
  const stored: StoredLead = {
    eventID: payload.eventID || newEventID(),
    email: payload.email,
    phone: payload.phone,
    firstName: payload.firstName,
    lastName: payload.lastName,
    tier: payload.tier,
    outcome: payload.outcome,
    ts: Date.now(),
  };
  try {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    /* ignore */
  }
  return stored;
}

export function readLeadEvent(): StoredLead | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredLead;
    // Discard events older than 30 minutes - booking page should fire shortly
    // after the survey completes; anything older is a stale tab.
    if (Date.now() - parsed.ts > 30 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

function userDataFromLead(lead: StoredLead): Record<string, string> {
  const ud: Record<string, string> = {};
  if (lead.email) ud.em = lead.email.trim().toLowerCase();
  if (lead.phone) ud.ph = lead.phone.replace(/\D/g, "");
  if (lead.firstName) ud.fn = lead.firstName.trim().toLowerCase();
  if (lead.lastName) ud.ln = lead.lastName.trim().toLowerCase();
  return ud;
}

/**
 * Fire the Meta Pixel "Lead" event. Pass the same eventID on every fire
 * (survey-complete + booking-page-load) so Meta dedupes them as one
 * conversion. Polls briefly if the base pixel script hasn't loaded yet.
 */
export function fireLeadEvent(lead: StoredLead): Promise<boolean> {
  return new Promise((resolve) => {
    const attempt = () => {
      const fbq = getFbq();
      if (!fbq) return false;
      try {
        const ud = userDataFromLead(lead);
        if (Object.keys(ud).length > 0) {
          // Advanced Matching - Meta hashes these client-side.
          fbq("init", META_PIXEL_ID, ud);
        }
        fbq(
          "track",
          "Lead",
          { currency: "USD", value: 1 },
          { eventID: lead.eventID },
        );
        console.info("[Meta Pixel] Lead fired", { eventID: lead.eventID });
        return true;
      } catch (e) {
        console.error("[Meta Pixel] Lead fire failed", e);
        return false;
      }
    };

    if (attempt()) {
      resolve(true);
      return;
    }
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts++;
      if (attempt()) {
        window.clearInterval(id);
        resolve(true);
      } else if (attempts > 60) {
        window.clearInterval(id);
        console.warn("[Meta Pixel] fbq never became available");
        resolve(false);
      }
    }, 100);
  });
}
/**
 * Fire the Meta Pixel "Schedule" event when a call is booked on the calendar.
 * Pass the same eventID used for the server-side CAPI Schedule call.
 */
export function fireScheduleEvent(eventID: string, lead?: StoredLead | null): boolean {
  const fbq = getFbq();
  if (!fbq) return false;
  try {
    if (lead) {
      const ud = userDataFromLead(lead);
      if (Object.keys(ud).length > 0) fbq("init", META_PIXEL_ID, ud);
    }
    fbq("track", "Schedule", { currency: "USD", value: 1 }, { eventID });
    console.info("[Meta Pixel] Schedule fired", { eventID });
    return true;
  } catch (e) {
    console.error("[Meta Pixel] Schedule fire failed", e);
    return false;
  }
}

export function newScheduleEventID(): string {
  return `schedule-${newEventID()}`;
}
